import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { useAuthStore } from '../store/authStore';
import { API_PATHS } from '../utils/apiPaths';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/moneyEu/api/v1';

/**
 * The API host to use when this page is served under the BEMI brand. This is a SEPARATE HOST, not
 * just a path prefix: a brand-free /api/gateway/... path on api.moneyeu.com still shows "moneyeu"
 * in the shopper's network tab, which defeats the point of white-labelling entirely.
 *
 * Deliberately a runtime hostname decision rather than a build-time one, so that ONE bundle serves
 * both brands. A VITE_BEMI_API_BASE_URL-only build would have to be built and deployed twice (once
 * per brand) and the two copies would drift; instead the same artifact is deployed to
 * login.moneyeu.com and login.bemi.uk and each decides for itself from the address the shopper
 * actually loaded. Adding a third brand is a hostname + base-URL pair here, never a new build.
 */
const BEMI_API_BASE_URL =
  import.meta.env.VITE_BEMI_API_BASE_URL || 'https://api.bemi.uk/api/gateway/v1';

/** True when this page is being served from a BEMI-branded host (login.bemi.uk, dev.login.bemi.uk). */
export function isWhiteLabelHost(): boolean {
  if (typeof window === 'undefined') return false;
  return /(^|\.)bemi\.uk$/i.test(window.location.hostname);
}

/**
 * Axios config every SHOPPER-FACING call must pass (hosted checkout, the 3thix inline onramp, and
 * the payment status page) so the brand of the URL follows the brand of the page.
 *
 * `undefined` on a MoneyEU host — callers then fall through to apiService's own default baseURL, so
 * this is a no-op for every existing merchant and changes behaviour only on bemi.uk. It must be
 * resolved from the hostname, not from anything in the order response, because the very first call
 * a checkout makes is the one that fetches that order.
 *
 * CRM/admin callers must NOT pass this: their session cookie is scoped to the MoneyEU host.
 */
export const CHECKOUT_API_CONFIG: AxiosRequestConfig | undefined = isWhiteLabelHost()
  ? { baseURL: BEMI_API_BASE_URL }
  : undefined;

/** The base URL shopper-facing calls resolve against — for building a backend URL a page navigates
 *  to, rather than one it calls through apiService. */
export const CHECKOUT_API_BASE_URL: string = isWhiteLabelHost() ? BEMI_API_BASE_URL : API_BASE_URL;

const PUBLIC_AUTH_PATHS = [
  API_PATHS.ADMIN_LOGIN_INITIATE,
  API_PATHS.ADMIN_LOGIN_VERIFY_OTP,
  API_PATHS.MERCHANT_LOGIN_INITIATE,
  API_PATHS.MERCHANT_LOGIN_VERIFY_OTP,
  API_PATHS.ADMIN_LOGOUT,
  API_PATHS.FORGOT_PASSWORD_INITIATE,
  API_PATHS.FORGOT_PASSWORD_VERIFY_OTP,
  API_PATHS.RESET_PASSWORD,
  API_PATHS.ADMIN_FORGOT_PASSWORD_INITIATE,
  API_PATHS.ADMIN_FORGOT_PASSWORD_VERIFY_OTP,
  API_PATHS.ADMIN_RESET_PASSWORD,
  API_PATHS.MERCHANT_FORGOT_PASSWORD_INITIATE,
  API_PATHS.MERCHANT_FORGOT_PASSWORD_VERIFY_OTP,
  API_PATHS.MERCHANT_RESET_PASSWORD,
  API_PATHS.MERCHANT_SIGNUP,
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  API_PATHS.CHECKOUT_CREATE_ORDER,
  API_PATHS.PAYMENT_REDIRECT_STATUS,
] as const;

let isRedirectingToLogin = false;

function isPublicAuthRequest(url: string | undefined): boolean {
  if (!url) return false;
  return PUBLIC_AUTH_PATHS.some((path) => url.includes(path));
}

function handleSessionExpired(): void {
  if (isRedirectingToLogin) return;

  const { isAuthenticated, logout } = useAuthStore.getState();
  if (!isAuthenticated) return;

  isRedirectingToLogin = true;
  logout();

  if (!window.location.pathname.startsWith('/login')) {
    window.location.replace('/login?session=expired');
  }
}

const inflightRequests = new Map<string, Promise<unknown>>();

function buildRequestKey(method: string, url: string, data?: unknown): string {
  return `${method.toUpperCase()}:${url}:${JSON.stringify(data ?? null)}`;
}

async function dedupedRequest<T>(key: string, request: () => Promise<T>): Promise<T> {
  const existing = inflightRequests.get(key);
  if (existing) {
    return existing as Promise<T>;
  }

  const promise = request().finally(() => {
    if (inflightRequests.get(key) === promise) {
      inflightRequests.delete(key);
    }
  });

  inflightRequests.set(key, promise);
  return promise;
}

class ApiService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true'
      },
    });

    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = useAuthStore.getState().token;
        if (token) {
          // A bearer token is only ever set for impersonation sessions (see utils/authMappers.ts /
          // the /impersonate bridge route). In that case, don't send the normal httpOnly session
          // cookie too — that cookie belongs to whichever admin is impersonating from this same
          // browser, and the backend's JwtAuthenticationFilter checks the cookie before the header.
          config.headers.Authorization = `Bearer ${token}`;
          config.withCredentials = false;
        } else {
          config.withCredentials = true;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && !isPublicAuthRequest(error.config?.url)) {
          handleSessionExpired();
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const key = buildRequestKey('GET', url, config?.params);
    return dedupedRequest(key, async () => {
      const response: AxiosResponse<T> = await this.axiosInstance.get(url, config);
      return response.data;
    });
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const key = buildRequestKey('POST', url, data);
    return dedupedRequest(key, async () => {
      const response: AxiosResponse<T> = await this.axiosInstance.post(url, data, config);
      return response.data;
    });
  }

  /** Full axios response — needed so callers can read Content-Disposition for file downloads. */
  async postBlob(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<Blob>> {
    return this.axiosInstance.post<Blob>(url, data, {
      timeout: 120000,
      ...config,
      responseType: 'blob',
    });
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const key = buildRequestKey('PUT', url, data);
    return dedupedRequest(key, async () => {
      const response: AxiosResponse<T> = await this.axiosInstance.put(url, data, config);
      return response.data;
    });
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const key = buildRequestKey('DELETE', url, config?.params);
    return dedupedRequest(key, async () => {
      const response: AxiosResponse<T> = await this.axiosInstance.delete(url, config);
      return response.data;
    });
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const key = buildRequestKey('PATCH', url, data);
    return dedupedRequest(key, async () => {
      const response: AxiosResponse<T> = await this.axiosInstance.patch(url, data, config);
      return response.data;
    });
  }
}

export const apiService = new ApiService();
